import React, { useState } from 'react';
import {
  Brain,
  BarChart3,
  FileBarChart,
  PieChart,
  TrendingUp,
  Target,
  DollarSign,
  MessageCircle,
  FileText,
  Search,
  AlertTriangle,
  Mic,
  Languages,
  Users2,
  ArrowRight,
  Zap,
  Globe,
  Settings
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const IntelligenceLanding = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const intelligenceFeatures = [
    // 1. USER INTERACTION & COMMUNICATION
    {
      id: 'ai-chat-assistant',
      title: 'AI Chat Assistant',
      description: 'Intelligent chat, smart suggestions & natural language queries',
      icon: <MessageCircle size={24} />,
      path: '/ai/chat-assistant',
      color: 'bg-indigo-50 text-indigo-600',
      features: ['Intelligent chat', 'Smart suggestions', 'Natural language queries']
    },
    {
      id: 'ai-search-qa',
      title: 'AI Search & Q&A',
      description: 'Natural language search, intelligent queries & context-aware results',
      icon: <Search size={24} />,
      path: '/ai/search',
      color: 'bg-cyan-50 text-cyan-600',
      features: ['Natural language search', 'Intelligent queries', 'Context-aware results']
    },

    // 2. DOCUMENT PROCESSING & INGESTION
    {
      id: 'document-ingestion',
      title: 'Document Ingestion',
      description: 'OCR processing, NLP extraction & metadata generation',
      icon: <FileText size={24} />,
      path: '/ai/ingestion',
      color: 'bg-lime-50 text-lime-600',
      features: ['OCR processing', 'NLP extraction', 'Metadata generation']
    },
    {
      id: 'multilingual-translation',
      title: 'Multilingual Translation',
      description: 'Auto-translation, multi-language support & context preservation',
      icon: <Languages size={24} />,
      path: '/ai/translation',
      color: 'bg-amber-50 text-amber-600',
      features: ['Auto-translation', 'Multi-language support', 'Context preservation']
    },

    // 3. CONTRACT CREATION & DRAFTING
    {
      id: 'ai-contract-drafting',
      title: 'AI Contract Drafting',
      description: 'Smart drafting, template generation & clause suggestions',
      icon: <FileText size={24} />,
      path: '/ai/drafting',
      color: 'bg-teal-50 text-teal-600',
      features: ['Smart drafting', 'Template generation', 'Clause suggestions']
    },
    {
      id: 'voice-to-contract',
      title: 'Voice-to-Contract',
      description: 'Speech recognition, contract generation & voice commands',
      icon: <Mic size={24} />,
      path: '/ai/voice-contract',
      color: 'bg-violet-50 text-violet-600',
      features: ['Speech recognition', 'Contract generation', 'Voice commands']
    },

    // 4. ANALYSIS & RISK ASSESSMENT
    {
      id: 'clause-risk-scoring',
      title: 'Clause Risk Scoring',
      description: 'Risk analysis, clause evaluation & compliance scoring',
      icon: <AlertTriangle size={24} />,
      path: '/ai/risk-scoring',
      color: 'bg-rose-50 text-rose-600',
      features: ['Risk analysis', 'Clause evaluation', 'Compliance scoring']
    },
    {
      id: 'ai-insights',
      title: 'AI Insights',
      description: 'Risk scoring, anomaly detection & smart summaries',
      icon: <Brain size={24} />,
      path: '/ai/insights',
      color: 'bg-yellow-50 text-yellow-600',
      features: ['Risk scoring', 'Anomaly detection', 'Smart summaries']
    },

    // 5. PREDICTIVE & STRATEGIC CAPABILITIES
    {
      id: 'predictive-analytics',
      title: 'Predictive Analytics',
      description: 'Renewal predictions, dispute forecasting & risk assessment',
      icon: <TrendingUp size={24} />,
      path: '/ai/analytics',
      color: 'bg-pink-50 text-pink-600',
      features: ['Renewal predictions', 'Dispute forecasting', 'Risk assessment']
    },
    {
      id: 'ai-negotiation',
      title: 'AI Negotiation Support',
      description: 'Negotiation strategies, clause suggestions & deal optimization',
      icon: <Users2 size={24} />,
      path: '/ai/negotiation',
      color: 'bg-emerald-50 text-emerald-600',
      features: ['Negotiation strategies', 'Clause suggestions', 'Deal optimization']
    }
  ];

  const stats = [
    { label: 'AI Models', value: '12', icon: <Brain size={20} /> },
    { label: 'Data Points', value: '2.4M', icon: <BarChart3 size={20} /> },
    { label: 'Predictions', value: '98.5%', icon: <Target size={20} /> },
    { label: 'Processing Speed', value: '1.2s', icon: <Zap size={20} /> }
  ];

  const filteredFeatures = intelligenceFeatures.filter((feature) => {
    const query = searchQuery.toLowerCase();
    return feature.title.toLowerCase().includes(query);
  });

  return (
    <>
      <style>{`
        .ai-search-input {
          width: 300px;
        }
        @media (max-width: 768px) {
          .ai-search-input {
            width: 100%;
          }
        }
      `}</style>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb 
        rightContent={
          <div style={{ position: 'relative' }} className="ai-search-input">
            <Search 
              size={18} 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280'
              }} 
            />
            <input
              type="text"
              placeholder="Search AI capabilities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold-primary)'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        }
      />
      
      {/* Page Header and Search Container */}
      <PageHeader
        title="AI Intelligence Hub"
        description="Harness the power of AI to drive intelligent decision-making. Get AI-powered insights, predictions, and automated assistance for your contracts and assets."
      />

      <div className="page-container">
        {/* Intelligence Capabilities */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {filteredFeatures.length > 0 ? (
            filteredFeatures.map((feature) => (
              <div
                key={feature.id}
                className="asset-landing-card"
                onClick={() => window.location.href = feature.path}
              >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: 'var(--gold-primary)' }} className="asset-card-icon">
                      {feature.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {feature.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {feature.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {feature.features.map((item, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '11px',
                          padding: '3px 6px',
                          background: '#FFF9E6',
                          color: 'var(--gold-primary)',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            ))
          ) : (
            <div style={{ 
              gridColumn: '1 / -1', 
              padding: '60px 20px', 
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px dashed #D1D5DB'
            }}>
              <Search size={32} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#374151', margin: '0 0 8px 0' }}>
                No results found
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                We couldn't find any AI capabilities matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IntelligenceLanding;
